import {
  Root,
  Portal,
  Overlay,
  DialogContentProps,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription,
  DialogProps,
} from '@radix-ui/react-dialog';
import { createContext, useContext } from 'react';
import { cn } from '../../utils';
import { X } from 'lucide-react';
import * as React from 'react';

const ModalContext = createContext({
  userCloseDisabled: false,
});

export const Modal = ({ children, ...otherProps }: DialogProps) => {
  return (
    <Root {...otherProps}>
      <Portal>
        <div className="sdk">
          {/*TODO @Mike make this app wide compatible with smaller ranges. Savanna has a default z-index 1000 on the header*/}
          <Overlay className="fixed z-[1001] inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 flex justify-center items-center">
            <ModalContext.Provider
              value={{ userCloseDisabled: !otherProps.onOpenChange }}
            >
              {children}
            </ModalContext.Provider>
          </Overlay>
        </div>
      </Portal>
    </Root>
  );
};

interface ModalContentProps extends DialogContentProps {
  title?: string;
  description?: string;
  className?: string;
}

export const ModalContent = ({
  className = '',
  title,
  description,
  children,
  ...otherProps
}: ModalContentProps) => {
  const { userCloseDisabled } = useContext(ModalContext);

  return (
    <DialogContent
      onOpenAutoFocus={(e) => e.preventDefault()}
      className={cn(
        'flex flex-col max-h-full px-6 overflow-hidden fixed left-[50%] top-[50%] z-[1001] w-full md:w-[668px] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
        className
      )}
      {...otherProps}
    >
      <div className="flex flex-col gap-1.5 text-center sm:text-left pt-6">
        {title && (
          <DialogTitle className="text-lg font-semibold leading-none tracking-tight">
            {title}
          </DialogTitle>
        )}
        {description && (
          <DialogDescription className="text-sm text-muted-foreground">
            {description}
          </DialogDescription>
        )}
      </div>
      <div className="flex-1 overflow-auto grid pb-6 -mr-6 pr-6">
        {children}
      </div>
      {!userCloseDisabled && (
        <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
      )}
    </DialogContent>
  );
};

export interface ModalContentCloseable {
  close: () => void;
}
